import React from 'react';
import { useUserInfo } from '@/provider/UserInfoProvider';
import { getAuth } from 'firebase/auth';
import { app } from '@/config/firebaseConfig';
import { useTranslations } from 'next-intl';

import {
  unsubscribeEndpoint,
  deleteAccountEndpoint,
  clearHistoryEndpoint,
} from '@/config/endpoints';
import { usePathname } from 'next/navigation';
import ConfirmationModal from '@/components/dashboard/confirmation/confirmationUnsubscribe';
import DeleteAccountConfirmation from '@/components/dashboard/confirmation/deleteAccountConfirmation';
import ClearHistoryConfirmation from '@/components/dashboard/confirmation/clearHistoryConfirmation';
import useRouteLogin from '@/ueshook/useRouteLogin';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import useRoute from '@/ueshook/useRoute';
import { kazeFetch } from '@/utils/kazeFetch';
import LanguageSelector from '@/components/ui/LanguageSelector';
import ApiManagement from '@/components/dashboard/apiManagement';
import { ApiTokenProvider } from '@/app/api-docs/context/apiTokenProvider';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'profile' | 'subscription' | 'api-management' | 'privacy';

const UserInfoModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  const { userInfo, isLoadingUser, updateUserInfoAssets } = useUserInfo();
  const email = getAuth(app).currentUser?.email || '';
  const pathname = usePathname();
  const { push } = useRoute();
  const { routerLogin } = useRouteLogin();

  // i18n translations
  const accountT = useTranslations('Dashboard.Account');
  const profileT = useTranslations('Dashboard.Profile');
  const subscriptionT = useTranslations('Dashboard.Subscription');
  const privacyT = useTranslations('Dashboard.Privacy');
  const loadingT = useTranslations('Dashboard.Loading');

  const [isConfirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = React.useState(false);
  const [isDeleteAccountModalOpen, setDeleteAccountModalOpen] =
    React.useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = React.useState(false);
  const [isClearHistoryModalOpen, setClearHistoryModalOpen] =
    React.useState(false);
  const [isClearingHistory, setIsClearingHistory] = React.useState(false);
  const [marketingEmails, setMarketingEmails] = React.useState(true);
  const [imageUploadHistory, setImageUploadHistory] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState<TabType>('profile');

  // 当模态框打开时，更新用户信息以获取最新的订阅状态
  React.useEffect(() => {
    if (isOpen) {
      updateUserInfoAssets();
    }
  }, [isOpen, updateUserInfoAssets, userInfo?.subscription]);

  const handleUnsubscription = async () => {
    try {
      setIsUnsubscribing(true);
      const userId = getAuth().currentUser?.uid;
      if (!userId) {
        routerLogin(() => pathname && push(pathname));
        return;
      }

      const response = await kazeFetch(unsubscribeEndpoint, { method: 'POST' });

      // 取消订阅成功，更新用户信息
      await updateUserInfoAssets();
    } catch (error) {
      console.error('Error unsubscribing:', error);

      // 即使 API 返回错误，也要更新用户信息以获取真实状态
      // 因为取消订阅可能已经成功，只是服务器返回了错误
      try {
        await updateUserInfoAssets();
      } catch (updateError) {
        console.error(
          'Failed to update user info after unsubscribe error:',
          updateError
        );
      }
    } finally {
      setIsUnsubscribing(false);
    }
  };

  const handleUnsubscribeClick = () => {
    setConfirmModalOpen(true);
  };

  const handleConfirmUnsubscribe = () => {
    setConfirmModalOpen(false);
    handleUnsubscription();
  };

  const handleCancelUnsubscribe = () => {
    setConfirmModalOpen(false);
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeletingAccount(true);
      const userId = getAuth().currentUser?.uid;
      if (!userId) {
        routerLogin(() => pathname && push(pathname));
        return;
      }

      const response = await kazeFetch(deleteAccountEndpoint, {
        method: 'POST',
        body: JSON.stringify({
          email: email,
        }),
      });

      // 删除账户成功，登出用户并跳转到首页
      await getAuth().signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleDeleteAccountClick = () => {
    setDeleteAccountModalOpen(true);
  };

  const handleConfirmDeleteAccount = () => {
    setDeleteAccountModalOpen(false);
    handleDeleteAccount();
  };

  const handleCancelDeleteAccount = () => {
    setDeleteAccountModalOpen(false);
  };

  const handleClearHistory = async () => {
    try {
      setIsClearingHistory(true);
      const userId = getAuth().currentUser?.uid;
      if (!userId) {
        routerLogin(() => pathname && push(pathname));
        return;
      }

      // 调用清除历史记录的API
      const response = await kazeFetch(clearHistoryEndpoint, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      console.log('clear history response', response);

      console.log('History cleared successfully');
    } catch (error) {
      console.error('Error clearing history:', error);
    } finally {
      setIsClearingHistory(false);
    }
  };

  const handleClearHistoryClick = () => {
    setClearHistoryModalOpen(true);
  };

  const handleConfirmClearHistory = () => {
    setClearHistoryModalOpen(false);
    handleClearHistory();
  };

  const handleCancelClearHistory = () => {
    setClearHistoryModalOpen(false);
  };

  // 侧边栏导航项
  const sidebarItems = [
    {
      id: 'profile' as TabType,
      label: accountT('tabs.profile'),
    },
    {
      id: 'subscription' as TabType,
      label: accountT('tabs.subscription'),
    },
    {
      id: 'api-management' as TabType,
      label: accountT('tabs.apiManagement'),
    },
    {
      id: 'privacy' as TabType,
      label: accountT('tabs.privacy'),
    },
  ];

  // 获取订阅显示名称
  const getSubscriptionDisplayName = (subscriptionName: string) => {
    switch (subscriptionName) {
      case 'free':
        return subscriptionT('free');
      case 'Get Pro (Monthly)':
        return subscriptionT('getProMonthly');
      case 'Pay as You Go':
        return subscriptionT('payAsYouGo');
      default:
        return subscriptionName;
    }
  };

  // 渲染主内容区域
  const renderContent = () => {
    if (isLoadingUser) {
      return (
        <div className='flex w-full items-center justify-center py-12'>
          <p className='text-gray-500'>{loadingT('text')}</p>
        </div>
      );
    }

    if (!userInfo) {
      return (
        <div className='flex w-full items-center justify-center py-12'>
          <p className='text-gray-500'>{loadingT('pleaseLogin')}</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'profile':
        return (
          <div className='space-y-6'>
            <div className='flex flex-col gap-1 text-left'>
              <h2 className='text-base font-medium'>{profileT('email')}</h2>
              <div className='flex flex-col justify-start gap-4 pt-2'>
                <p className='text-base text-gray-800'>{email}</p>
              </div>
            </div>
            <LanguageSelector />

            {/* Delete Account Section */}
            <div className='space-y-4'>
              <div className='flex flex-col gap-1 text-left'>
                <h2 className='text-base font-medium'>
                  {profileT('deleteAccount')}
                </h2>
                <div className='flex flex-col justify-start gap-4 pt-2 md:flex-row md:items-center md:justify-between md:gap-0'>
                  <div className='flex-1'>
                    <div className='text-sm text-gray-600'>
                      <p>{profileT('deleteAccountDescription')}</p>
                      <p>{profileT('deleteAccountWarning')}</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleDeleteAccountClick}
                    disabled={isDeletingAccount}
                    variant='outline'
                    size='sm'
                    className='hover:text-red-60 w-fit border-red-300 text-red-500 hover:bg-red-50'
                  >
                    {isDeletingAccount
                      ? profileT('processing')
                      : profileT('deleteAccountButton')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'subscription':
        return (
          <div className='space-y-6'>
            {/* Subscription Status with Manage Dropdown */}
            <div className='flex flex-col gap-1 text-left'>
              <div className='flex items-center justify-between'>
                <h2 className='text-lg font-semibold'>
                  {getSubscriptionDisplayName(
                    userInfo.subscription.subscription_name
                  )}
                </h2>
                {userInfo.subscription.in_subscription && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='outline'
                        className='hidden items-center gap-2 md:flex'
                      >
                        {subscriptionT('manage')}
                        <ChevronDown className='h-4 w-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end' className='w-48'>
                      <DropdownMenuItem
                        onClick={() => {
                          push('/pricing');
                          onClose();
                        }}
                        className='cursor-pointer'
                      >
                        {subscriptionT('upgradePlan')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleUnsubscribeClick}
                        disabled={isUnsubscribing}
                        className='cursor-pointer text-red-500 focus:text-red-600'
                      >
                        {isUnsubscribing
                          ? profileT('processing')
                          : subscriptionT('cancelSubscription')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <div className='flex flex-col justify-start gap-4'>
                {userInfo.subscription.subscription_name !== 'free' && (
                  <p className='text-sm text-gray-600'>
                    {subscriptionT('autoRenews')}{' '}
                    {userInfo.subscription.end_at
                      ? new Date(
                          userInfo.subscription.end_at
                        ).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : 'N/A'}
                  </p>
                )}
                {userInfo.subscription.subscription_name === 'free' && (
                  <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
                    <p className='text-sm text-gray-600'>
                      {subscriptionT('needMoreCredits')}
                    </p>
                    <Button
                      onClick={() => {
                        push('/pricing');
                        onClose();
                      }}
                      className='hidden w-full bg-[#6137E0] text-white hover:bg-[#5028C7] md:block md:w-auto'
                    >
                      {subscriptionT('upgradePlan')}
                    </Button>
                  </div>
                )}
                <div className='flex flex-col gap-1 text-left'>
                  <h2 className='mt-1 text-base font-medium text-gray-900'>
                    {subscriptionT('creditsRemaining')}{' '}
                    {userInfo.plan.available_count}
                  </h2>
                </div>
                {userInfo.subscription.subscription_name === 'free' && (
                  <Button
                    onClick={() => {
                      push('/pricing');
                      onClose();
                    }}
                    className='block w-full bg-[#6137E0] text-white hover:bg-[#5028C7] md:hidden md:w-auto'
                  >
                    {subscriptionT('upgradePlan')}
                  </Button>
                )}
                {/* Mobile Manage Button - Full width below credits */}
                {userInfo.subscription.in_subscription && (
                  <div className='md:hidden'>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant='outline'
                          className='flex w-full items-center justify-center gap-2'
                        >
                          {subscriptionT('manage')}
                          <ChevronDown className='h-4 w-4' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='center' className='w-48'>
                        <DropdownMenuItem
                          onClick={() => {
                            push('/pricing');
                            onClose();
                          }}
                          className='cursor-pointer'
                        >
                          {subscriptionT('upgradePlan')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={handleUnsubscribeClick}
                          disabled={isUnsubscribing}
                          className='cursor-pointer text-red-500 focus:text-red-600'
                        >
                          {isUnsubscribing
                            ? profileT('processing')
                            : subscriptionT('cancelSubscription')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            </div>

            {/* AI Headshot Plan */}
            <div className='flex flex-col gap-1 text-left'>
              <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
                <div className='flex-1'>
                  <h2 className='mt-3 text-lg font-semibold'>
                    {userInfo.plan.id_photo.basic_limit > 0 ||
                    userInfo.plan.id_photo.pro_limit > 0
                      ? subscriptionT('aiHeadshotPlanPro')
                      : subscriptionT('aiHeadshotPlan')}
                  </h2>
                  <p className='mb-0 mt-3 text-sm text-gray-600'>
                    {subscriptionT('aiHeadshotDescription')}
                  </p>
                  {(userInfo.plan.id_photo.basic_limit > 0 ||
                    userInfo.plan.id_photo.pro_limit > 0) && (
                    <>
                      {userInfo.plan.id_photo.basic_limit > 0 && (
                        <p className='mt-2 text-base font-medium text-gray-900'>
                          {subscriptionT('basicRemaining')}{' '}
                          {userInfo.plan.id_photo.basic_limit}
                        </p>
                      )}
                      {userInfo.plan.id_photo.pro_limit > 0 && (
                        <>
                          <Separator className='my-2' />
                          <p className='text-base font-medium text-gray-900'>
                            {subscriptionT('proRemaining')}{' '}
                            {userInfo.plan.id_photo.pro_limit}
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className='flex w-full flex-col gap-2 md:w-auto'>
                  <Button
                    onClick={() => {
                      push('/pricing?tab=headshots');
                      onClose();
                    }}
                    className='w-full bg-[#6137E0] text-white hover:bg-[#5028C7]'
                  >
                    {userInfo.subscription.subscription_name === 'Pay as You Go'
                      ? subscriptionT('buyPackages')
                      : subscriptionT('explorePlans')}
                  </Button>
                  {userInfo.subscription.subscription_name ===
                    'Get Pro (Monthly)' && (
                    <Button
                      variant='outline'
                      onClick={() => {
                        push('/pricing');
                        onClose();
                      }}
                      className='w-full'
                    >
                      {subscriptionT('buyPackages')}
                    </Button>
                  )}
                  {userInfo.subscription.subscription_name ===
                    'Pay as You Go' && (
                    <Button
                      variant='outline'
                      onClick={() => {
                        push('/pricing');
                        onClose();
                      }}
                      className='w-full'
                    >
                      {subscriptionT('upgrade')}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Need help with billing */}
            <div className='justify-left flex pt-1'>
              <button
                onClick={() => {
                  push('/get-help?type=credits_subscription');
                  onClose();
                }}
                className='cursor-pointer text-[#6137E0] hover:text-[#5028C7]'
              >
                {subscriptionT('needHelpBilling')}
              </button>
            </div>
          </div>
        );

      case 'api-management':
        return (
          <ApiTokenProvider>
            <ApiManagement onClose={onClose} />
          </ApiTokenProvider>
        );

      case 'privacy':
        return (
          <div className='space-y-6'>
            {/* Marketing Emails */}
            <div className='flex flex-col gap-1 text-left'>
              <div className='flex items-center justify-between'>
                <div className='flex-1'>
                  <h2 className='text-lg font-semibold'>
                    {privacyT('marketingEmails')}
                  </h2>
                  <p className='text-sm text-gray-600'>
                    {privacyT('marketingEmailsDescription')}
                  </p>
                </div>
                <div className='flex items-center'>
                  <Switch
                    checked={marketingEmails}
                    onCheckedChange={setMarketingEmails}
                  />
                </div>
              </div>
            </div>

            {/* Image Upload History */}
            <div className='flex flex-col gap-1 text-left'>
              <div className='flex items-center justify-between'>
                <div className='flex-1'>
                  <h2 className='text-lg font-semibold'>
                    {privacyT('imageUploadHistory')}
                  </h2>
                  <p className='max-w-md text-sm text-gray-600'>
                    {privacyT('imageUploadHistoryDescription')}
                  </p>
                </div>
                <div className='flex items-center'>
                  <Switch
                    checked={imageUploadHistory}
                    onCheckedChange={setImageUploadHistory}
                  />
                </div>
              </div>
            </div>

            {/* Clear All History */}
            <div className='flex flex-col gap-1 text-left'>
              <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
                <div className='flex-1'>
                  <h2 className='text-lg font-semibold'>
                    {privacyT('clearAllHistory')}
                  </h2>
                  <p className='max-w-md text-sm text-gray-600'>
                    {privacyT('clearAllHistoryDescription')}
                  </p>
                </div>
                <Button
                  onClick={handleClearHistoryClick}
                  disabled={isClearingHistory}
                  variant='outline'
                  size='sm'
                  className='w-fit border-red-300 text-red-500 hover:bg-red-50 hover:text-red-600'
                >
                  {isClearingHistory
                    ? profileT('processing')
                    : privacyT('clearAllHistoryButton')}
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='flex h-screen w-full max-w-5xl flex-col gap-0 rounded-lg bg-white p-0 md:h-auto md:max-h-[calc(100vh-100px)] md:w-11/12'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
          <DialogTitle className='text-2xl font-semibold'>
            {accountT('title')}
          </DialogTitle>
        </div>

        {/* Mobile Tab Navigation */}
        <div className='border-b border-gray-200 bg-white md:hidden'>
          <div className='flex overflow-x-auto px-4 py-2'>
            {sidebarItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex-shrink-0 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className='flex min-h-[500px] flex-1'>
          {/* Desktop Sidebar */}
          <div className='hidden w-64 border-r border-gray-200 bg-white md:block'>
            <nav className='p-4'>
              <ul className='space-y-2'>
                {sidebarItems.map((item) => {
                  const isActive = activeTab === item.id;

                  return (
                    <li key={item.id}>
                      <button
                        onClick={() => setActiveTab(item.id)}
                        className={`flex w-full items-center rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        {item.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Content Area */}
          <div className='flex-1 overflow-y-auto p-6'>{renderContent()}</div>
        </div>

        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          onConfirm={handleConfirmUnsubscribe}
          onCancel={handleCancelUnsubscribe}
          isLoading={isUnsubscribing}
        />

        <DeleteAccountConfirmation
          isOpen={isDeleteAccountModalOpen}
          onClose={handleCancelDeleteAccount}
          onConfirm={handleConfirmDeleteAccount}
          userEmail={email}
          isLoading={isDeletingAccount}
        />

        <ClearHistoryConfirmation
          isOpen={isClearHistoryModalOpen}
          onClose={handleCancelClearHistory}
          onConfirm={handleConfirmClearHistory}
          isLoading={isClearingHistory}
        />
      </DialogContent>
    </Dialog>
  );
};

export default UserInfoModal;
